import { assign, createMachine, fromPromise } from 'xstate'

import Api from '@/api/_legacy'
import { ApiDfm, ApiDfmWithUsers, UserInformationBasicDfm } from '@/api/_legacy/generated-api'

const TENANT_ID = 'absa'

export type DfmsState = {
  dfmUsers?: UserInformationBasicDfm[]
  dfms: ApiDfm[]
  error?: any
  filteredDfms: ApiDfmWithUsers[]
  initialActiveTab: 'details' | 'users'
  isDfmSavedSuccessfully: boolean
  selectedDfm?: ApiDfmWithUsers
  selectedDfmUser?: UserInformationBasicDfm
}

type SelectDfmEvent = { dfmId: string; type: 'SELECT_DFM' }
type FetchDfmsEvent = { tenant: string; type: 'FETCH_DFMS' }
type SearchDfmsEvent = {
  searchTerm: string
  type: 'SEARCH_DFMS'
}
type AddDfmCompleteEvent = { type: 'ADD_DFM_COMPLETE' }
type DfmSaved = { type: 'DFM_SAVED' }
type ResetSavedIndicator = { type: 'RESET_SAVED_INDICATOR' }

type DfmsEvent =
  | SelectDfmEvent
  | FetchDfmsEvent
  | SearchDfmsEvent
  | AddDfmCompleteEvent
  | DfmSaved
  | ResetSavedIndicator

export const DfmsMachine = createMachine(
  {
    types: {} as {
      context: DfmsState
      events: DfmsEvent
    },
    id: 'dfm-machine',
    initial: 'idle',
    context: {
      filteredDfms: [],
      dfms: [],
      initialActiveTab: 'details',
      isDfmSavedSuccessfully: false
    },
    states: {
      idle: {
        on: {
          FETCH_DFMS: {
            target: 'loading'
          }
        }
      },
      dfmSelected: {
        initial: 'dfmDetails',
        states: {
          dfmDetails: {
            initial: 'untouched',
            states: {
              untouched: {}
            }
          }
        },
        on: {
          ADD_DFM_COMPLETE: {
            target: 'loading'
          },
          SEARCH_DFMS: {
            actions: ['filterDfms'],
            target: 'dfmsLoaded'
          },
          SELECT_DFM: {
            target: 'loadingDfmWithUsers'
          },
          DFM_SAVED: {
            target: 'reload',
            actions: ['setDfmDetailsTab', 'setDfmSaveSuccessful']
          },
          RESET_SAVED_INDICATOR: {
            actions: ['resetSavedIndicator']
          }
        }
      },
      loading: {
        invoke: [
          {
            id: 'getDfms',
            src: 'fetchDfms',
            input: ({ context, event }) => ({ context, event }),
            onDone: {
              target: 'dfmsLoaded',
              actions: ['setDfms']
            },
            onError: {
              target: 'failure',
              actions: assign({ error: ({ event }) => event.error })
            }
          }
        ]
      },
      loadingDfmWithUsers: {
        invoke: [
          {
            id: 'getDfmWithUsers',
            src: 'fetchDfmWithUsers',
            input: ({ context, event }) => ({ context, event }),
            onDone: {
              target: 'dfmSelected',
              actions: ['selectDfm']
            },
            onError: {
              target: 'failure',
              actions: assign({ error: ({ event }) => event.error })
            }
          }
        ]
      },
      reload: {
        invoke: {
          id: 'getDfms',
          src: 'fetchDfms',
          input: ({ context, event }) => ({ context, event }),
          onDone: {
            target: 'dfmSelected',
            actions: ['setDfms', 'reselectPreviouslySelectedDfm']
          },
          onError: {
            target: 'failure',
            actions: assign({ error: ({ event }) => event.error })
          }
        }
      },
      dfmsLoaded: {
        on: {
          SELECT_DFM: {
            target: 'loadingDfmWithUsers'
          },
          SEARCH_DFMS: {
            actions: ['filterDfms'],
            target: 'dfmsLoaded'
          },
          ADD_DFM_COMPLETE: {
            target: 'loading'
          }
        }
      },
      failure: {}
    }
  },
  {
    actors: {
      fetchDfms: fromPromise(async () => {
        const response = await Api.usermanager.currentList()

        const data = response.data.dfms

        return data
      }),
      fetchDfmWithUsers: fromPromise(async ({ input }: any) => {
        const event = input.event as SelectDfmEvent
        const response = await Api.tenant.usersDetail4(event.dfmId, TENANT_ID)

        const { data } = response

        return { selectedDfm: data }
      })
    },
    actions: {
      setDfms: assign(({ context, event }: any) => ({
        ...context,
        dfms: event.output,
        filteredDfms: event.output
      })),
      selectDfm: assign(({ context, event }: any) => {
        const newDfms = [...context.filteredDfms]

        return {
          ...context,
          filteredDfms: newDfms,
          selectedDfm: event.output?.selectedDfm
        }
      }),
      reselectPreviouslySelectedDfm: assign(({ context }) => {
        if (!context.selectedDfm) {
          return context
        }

        const newDfms = [...context.filteredDfms]
        const index = newDfms.findIndex(x => x.dfmId === context.selectedDfm?.dfmId)
        const dfm = { ...newDfms[index] }
        newDfms[index] = dfm

        return {
          ...context,
          filteredDfms: newDfms,
          selectedDfm: dfm
        }
      }),
      setDfmSaveSuccessful: assign(context => ({
        ...context,
        isDfmSavedSuccessfully: true
      })),
      resetSavedIndicator: assign(context => ({
        ...context,
        isDfmSavedSuccessfully: false
      })),
      filterDfms: assign(({ context, event }: any) => ({
        ...context,
        filteredDfms: context.dfms.filter(x =>
          x.dfmName.toLowerCase().includes(event.searchTerm.toLowerCase())
        )
      })),
      setDfmDetailsTab: assign(context => ({
        ...context,
        initialActiveTab: 'details'
      }))
    }
  }
)
